'use client';

import { faArrowLeft, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LaboratoryExamItem } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/laboratory-exam-item.component';
import { LaboratoryExamsFooter } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/laboratory-exams-footer.component';
import { UseLaboratoryExamSelectionReturn } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/_hooks/use-laboratory-exam-selection.hook';
import { useLaboratoryExams } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/_hooks/use-laboratory-exams.hook';
import { LaboratoryExamsFlowKeys } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/laboratory-exams-flow.types';
import { LaboratoryErrorFallback } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-error-fallback.component';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { Skeleton } from '@/components/ui/skeleton.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';

const SKELETON_ROWS = 6;

function examsScrollSpacing(count: number, isFull: boolean): string {
  if (count === 0) {
    return '';
  }

  return isFull ? 'pb-40' : 'pb-36';
}

interface LaboratoryExamsStepProps {
  facilityId: string;
  laboratoryName: string;
  selection: UseLaboratoryExamSelectionReturn;
  canReturnToCoverage?: boolean;
}

export function LaboratoryExamsStep({
  facilityId,
  laboratoryName,
  selection,
  canReturnToCoverage = false,
}: LaboratoryExamsStepProps) {
  const { handleJump, onClose } = useProgressiveModal<LaboratoryExamsFlowKeys>();
  const {
    exams,
    term,
    setTerm,
    isLoading,
    isLoadingMore,
    hasMore,
    hasError,
    hasLoadMoreError,
    loadMore,
    retry,
    retryLoadMore,
  } = useLaboratoryExams(facilityId);

  const showsEmptyState = !isLoading && !hasError && exams.length === 0;

  const handleBack = () => {
    if (canReturnToCoverage) {
      handleJump({ key: LaboratoryExamsFlowKeys.COVERAGE, returning: true });

      return;
    }

    onClose();
  };

  return (
    <div className="relative flex flex-col h-full min-h-0" data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL}>
      <div className="flex flex-col gap-4 px-6 pt-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={canReturnToCoverage ? 'Voltar' : 'Fechar'}
            className="size-10 shrink-0 flex items-center justify-center text-gray-500 hover:text-gray-900"
            data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_CLOSE}
            onClick={handleBack}
          >
            <Icon icon={faArrowLeft} size="lg" />
          </button>

          <Input
            data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_SEARCH}
            aria-label="Busque pelo exame"
            placeholder="Busque pelo exame"
            startIcon={faMagnifyingGlass}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        <Typography variant={TypographyVariant.BODY_TEXT} as="p" className="font-bold text-gray-700">
          {laboratoryName}
        </Typography>
      </div>

      <div className={`flex-1 overflow-y-auto ${examsScrollSpacing(selection.count, selection.isFull)}`}>
        {isLoading && (
          <div className="flex flex-col gap-4 px-6 py-4" role="status" aria-label="Carregando exames">
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        )}

        {!isLoading && hasError && (
          <LaboratoryErrorFallback
            message="Não foi possível carregar os exames."
            laboratoryId={facilityId}
            dataTestId={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_ERROR}
            onRetry={retry}
          />
        )}

        {showsEmptyState && (
          <Typography
            variant={TypographyVariant.BODY_TEXT}
            as="p"
            data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_EMPTY}
            className="px-6 py-10 text-center text-gray-500"
          >
            Nenhum exame encontrado.
          </Typography>
        )}

        {exams.map((exam) => (
          <LaboratoryExamItem
            key={exam.id}
            exam={exam}
            checked={selection.isSelected(exam.id)}
            disabled={selection.isFull && !selection.isSelected(exam.id)}
            onToggle={selection.toggle}
          />
        ))}

        {hasLoadMoreError && (
          <div
            role="alert"
            data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_LOAD_MORE_ERROR}
            className="flex flex-col items-center gap-2 px-6 pt-4"
          >
            <Typography variant={TypographyVariant.BODY_TEXT} className="text-center text-gray-500">
              Não foi possível carregar mais exames.
            </Typography>
            <Button variant="secondary" isLoading={isLoadingMore} onClick={retryLoadMore}>
              Tentar novamente
            </Button>
          </div>
        )}

        {hasMore && !hasLoadMoreError && (
          <div className="px-6 py-4">
            <Button
              variant="secondary"
              className="w-full"
              isLoading={isLoadingMore}
              disabled={isLoadingMore}
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_LOAD_MORE}
              onClick={loadMore}
            >
              Mostrar mais exames
            </Button>
          </div>
        )}
      </div>

      <LaboratoryExamsFooter
        count={selection.count}
        total={selection.total}
        isFull={selection.isFull}
        onSeeSchedules={() => handleJump({ key: LaboratoryExamsFlowKeys.SCHEDULES })}
      />
    </div>
  );
}
